// Sprint notification-center (spec D2) — Web Push subscription helpers.
//
// iOS ≥16.4 supports Web Push ONLY for installed PWAs and ONLY after a
// user-gesture-triggered Notification.requestPermission(). The Settings
// page owns that gesture ("Enable on this device"); this module owns
// the mechanics: fetch the VAPID public key, subscribe via the active
// service-worker registration, sync with the backend.
//
// New (verified no equivalent exists).

import { api } from './api';

export type PushSupport =
  | 'unsupported' // browser has no push API (or no SW registration possible)
  | 'denied' // user permanently denied notifications for this origin
  | 'ready'; // supported; may or may not be subscribed yet

export function pushSupport(): PushSupport {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  return 'ready';
}

// The Push API wants the VAPID public key as a Uint8Array.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

// 21-09-2026 (infra-ops, PUSH A PAU v1.1): el SW lo registra main.tsx al
// arrancar (lib/sw-register.ts → /sw.js). `pushManager.subscribe` exige un SW
// ACTIVO (Chrome: "no active Service Worker"); si Pau pulsa "Activar" antes
// de que termine de activarse, se espera a `serviceWorker.ready` con tope de
// 5 s en vez de fallar en silencio.
async function registration(): Promise<ServiceWorkerRegistration | null> {
  try {
    const reg = (await navigator.serviceWorker.getRegistration()) ?? null;
    if (!reg) return null;
    if (reg.active) return reg;
    const ready = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
    ]);
    return ready ?? reg;
  } catch {
    return null;
  }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() === 'unsupported') return null;
  const reg = await registration();
  if (!reg) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Must be called from a user gesture (iOS requirement). Returns true on
// success. Throws are swallowed into `false` — the caller shows the
// founder-mode failure copy, not an exception.
export async function subscribeToPush(): Promise<boolean> {
  try {
    if (pushSupport() !== 'ready') return false;
    const { enabled, publicKey } = await api<{ enabled: boolean; publicKey: string }>(
      '/api/push/public-key',
    );
    if (!enabled || !publicKey) return false;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;
    const reg = await registration();
    if (!reg) return false;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
    });
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;
    await api('/api/push/subscriptions', {
      method: 'POST',
      body: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      },
    });
    return true;
  } catch {
    return false;
  }
}

// Sprint test-notification-button (spec D1) — fire a real Tier A test
// notification through the production path (feed + WS + toast + Web Push),
// scoped server-side to the caller's own devices. No body: the target is
// the session user, resolved on the server (spec D2). Throws ApiHttpError
// on failure so the caller can branch on the code (`RATE_LIMITED` → the
// "wait a few seconds" copy, spec D4).
export async function sendTestNotification(): Promise<void> {
  await api('/api/push/test', { method: 'POST' });
}

// 02-11: el servidor apunta cada aparato por la CUENTA de la sesión (antes,
// todos como el mismo usuario), para que el aviso de prueba llegue solo a los
// aparatos de quien lo pide. Un aparato apuntado antes de ese cambio se
// vuelve a apuntar aquí, en cada arranque de la web, si ya tenía permiso: sin
// gesto de nadie. Sin sesión (401) o sin permiso, no pasa nada.
export async function resyncPushSubscription(): Promise<void> {
  try {
    if (pushSupport() !== 'ready' || Notification.permission !== 'granted') return;
    const sub = await getCurrentSubscription();
    const json = sub?.toJSON();
    if (!json?.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
    await api('/api/push/subscriptions', {
      method: 'POST',
      body: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
    });
  } catch {
    /* mejor esfuerzo: la siguiente carga lo vuelve a intentar */
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const sub = await getCurrentSubscription();
    if (!sub) return true;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    await api('/api/push/subscriptions', { method: 'DELETE', body: { endpoint } });
    return true;
  } catch {
    return false;
  }
}
